/**
 * Animal ABCs, 123s — entry point.
 *
 * The user lands directly on the Alphabet (AnimalAlphabetGrid). From there,
 * sibling-CTA pills inside each sub-game route between Alphabet, Counting,
 * and Names without an interstitial menu screen.
 */

import { useState, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { englishAnimals, spanishAnimals } from '../../data/animalABCs';
import { VoiceDrawingCanvas } from './VoiceDrawingCanvas';
import { AnimalAlphabetGrid } from './AnimalAlphabetGrid';
import { CountingNatureGame } from './CountingNatureGame';
import { NameWithAnimals } from './NameWithAnimals';

type GameMode =
  | 'animal-alphabet'
  | 'trace-letters'
  | 'counting-nature'
  | 'trace-numbers'
  | 'name-animals';
type LetterOrder = 'alphabetical' | 'random';

export function AnimalABCs() {
  const { language } = useLanguage();
  const [gameMode, setGameMode] = useState<GameMode>('animal-alphabet');
  const [letterOrder, setLetterOrder] = useState<LetterOrder>('alphabetical');
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);

  const animals = language === 'es' ? spanishAnimals : englishAnimals;
  const letters = useMemo(
    () =>
      letterOrder === 'alphabetical'
        ? animals
        : [...animals].sort(() => Math.random() - 0.5),
    [letterOrder, animals]
  );
  const currentLetter = letters[currentLetterIndex]?.letter || 'A';
  const currentAnimal = letters[currentLetterIndex]?.animal || '';

  const t = {
    back: language === 'es' ? 'Volver' : 'Back',
    comingSoon: language === 'es' ? 'Próximamente…' : 'Coming soon…',
  };

  const goToTraceLetters = () => {
    setLetterOrder('alphabetical');
    setCurrentLetterIndex(0);
    setGameMode('trace-letters');
  };

  return (
    <div className="min-h-screen">
      {gameMode === 'animal-alphabet' && (
        <AnimalAlphabetGrid
          onTraceLetters={goToTraceLetters}
          onGoCounting={() => setGameMode('counting-nature')}
          onGoNames={() => setGameMode('name-animals')}
        />
      )}

      {gameMode === 'trace-letters' && (
        <VoiceDrawingCanvas
          letter={currentLetter}
          animal={currentAnimal}
          onBack={() => setGameMode('animal-alphabet')}
          onNext={() => {
            if (letterOrder === 'alphabetical') {
              setCurrentLetterIndex((prev) =>
                prev + 1 >= letters.length ? 0 : prev + 1
              );
            } else {
              setCurrentLetterIndex(
                Math.floor(Math.random() * letters.length)
              );
            }
          }}
          language={language as 'en' | 'es'}
          letterOrder={letterOrder}
          onLetterOrderChange={(order) => {
            setLetterOrder(order);
            if (order === 'random') {
              setCurrentLetterIndex(Math.floor(Math.random() * letters.length));
            } else {
              setCurrentLetterIndex(0);
            }
          }}
        />
      )}

      {gameMode === 'counting-nature' && (
        <CountingNatureGame
          onGoAlphabet={() => setGameMode('animal-alphabet')}
          onGoNames={() => setGameMode('name-animals')}
        />
      )}

      {gameMode === 'name-animals' && (
        <NameWithAnimals
          onGoAlphabet={() => setGameMode('animal-alphabet')}
          onGoCounting={() => setGameMode('counting-nature')}
        />
      )}

      {gameMode === 'trace-numbers' && (
        <div className="min-h-screen p-4 md:p-6" style={{ background: '#fbfbe8' }}>
          <div className="max-w-3xl mx-auto pt-6 md:pt-12">
            <button
              onClick={() => setGameMode('animal-alphabet')}
              className="mb-6"
              style={{
                padding: '12px 26px',
                borderRadius: 999,
                background: 'transparent',
                color: '#42a57a',
                border: '1.5px solid #42a57a',
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← {t.back}
            </button>
            <div
              className="p-8 md:p-12 text-center"
              style={{
                background: '#ffffff',
                border: '1px solid #e8e0c8',
                borderRadius: 24,
              }}
            >
              <p
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: 'clamp(22px, 3vw, 32px)',
                  color: '#2a6d5b',
                }}
              >
                {t.comingSoon}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
